/** Client-side mirror of BACKEND sharePointFolderPath canonicalizer (guidance only; server revalidates). */

export type FolderPathMode = 'project_relative' | 'library_relative';

const ILLEGAL_SEGMENT_CHARS = /["*:<>?|]/;
const CONTROL_CHARS = /[\x00-\x1F\x7F]/;

export function canonicalizeFolderPath(
  rawPath: string | null | undefined,
  mode: FolderPathMode = 'project_relative'
): { valid: boolean; normalized: string | null; message?: string } {
  const trimmed = rawPath == null ? '' : String(rawPath).trim();
  if (!trimmed) {
    if (mode === 'library_relative') {
      return {
        valid: false,
        normalized: null,
        message: 'Secondary library folder path requires at least one folder segment',
      };
    }
    return { valid: true, normalized: null };
  }

  let normalized = trimmed.replace(/\\/g, '/');
  const lower = normalized.toLowerCase();
  if (/^[a-z]:/.test(lower)) {
    return { valid: false, normalized: null, message: `Folder path "${trimmed}" is not a valid relative path (drive-letter)` };
  }
  if (normalized.startsWith('//') || normalized.startsWith('\\\\')) {
    return { valid: false, normalized: null, message: `Folder path "${trimmed}" is not a valid relative path (unc-path)` };
  }
  if (lower.startsWith('http://') || lower.startsWith('https://') || lower.startsWith('file:')) {
    return { valid: false, normalized: null, message: `Folder path "${trimmed}" is not a valid relative path (url)` };
  }
  if (normalized.includes('//')) {
    return { valid: false, normalized: null, message: `Folder path "${trimmed}" contains an empty segment` };
  }
  if (normalized.startsWith('/')) {
    normalized = normalized.substring(1);
  }
  if (normalized.endsWith('/')) {
    normalized = normalized.replace(/\/+$/, '');
  }
  if (!normalized) {
    if (mode === 'library_relative') {
      return {
        valid: false,
        normalized: null,
        message: 'Secondary library folder path cannot be library root only; provide at least one folder segment',
      };
    }
    return { valid: true, normalized: null };
  }
  if (normalized.length > 500) {
    return { valid: false, normalized: null, message: 'Folder path exceeds 500 characters' };
  }
  for (const part of normalized.split('/')) {
    if (!part || part === '.' || part === '..') {
      return { valid: false, normalized: null, message: `Folder path "${trimmed}" is invalid at segment "${part}"` };
    }
    if (part.length > 255 || ILLEGAL_SEGMENT_CHARS.test(part) || CONTROL_CHARS.test(part) || part.endsWith('.') || part.endsWith(' ')) {
      return { valid: false, normalized: null, message: `Folder path "${trimmed}" is invalid at segment "${part}"` };
    }
  }
  return { valid: true, normalized };
}
